'use client';

import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Cpu,
  CreditCard,
  Database,
  Globe,
  Loader2,
  Lock,
  MapPin,
  Server,
  Ticket,
  X,
} from 'lucide-react';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { settings } from '../../../config/settings';
import {
  apiCheckServerName,
  apiCreatePayPalSubscription,
  apiCreateServer,
  calculatePrice,
} from '../../helpers/hosting';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import ResourceSlider from '@/components/ResourceSlider';
import { BooleanToggle } from '@/components/hosting/ConfigControls';
import { supportsViaVersion } from '@/lib/hosting/serverOptions';
import { AdWrapper } from '@/components/Ads';
import { useTranslations } from 'next-intl';
import { useLabel } from '@/helpers/useLabel';

const steps = [
  { id: 'details', label: 'Server Details' },
  { id: 'plan', label: 'Resources & Billing' },
  { id: 'review', label: 'Review & Launch' },
];

function formatMoney(value: number | string) {
  // MySQL DECIMAL columns arrive as strings, so coerce before toFixed.
  const amount = Number(value);
  return `$${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
}

function CreatePageContent() {
  const t = useTranslations('Auto.Create');
  const tl = useLabel();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [stripeConfig, setStripeConfig] = useState<{ enabled: boolean; publishableKey: string | null }>({
    enabled: false,
    publishableKey: null,
  });
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);

  const [serverName, setServerName] = useState('');
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null);
  const [nameError, setNameError] = useState('');
  const [checkingName, setCheckingName] = useState(false);
  const [isPublic, setIsPublic] = useState(false);

  // A server starts from either a loader + Minecraft version ("version") or a prebuilt
  // template ("template", not shipped yet).
  const [baseMode, setBaseMode] = useState<'version' | 'template'>('version');
  const [selectedJarType, setSelectedJarType] = useState('paper');
  const [selectedMcVersion, setSelectedMcVersion] = useState('');
  const [jarCatalog, setJarCatalog] = useState<any[]>([]);
  const [jarsLoading, setJarsLoading] = useState(true);
  const [jarsError, setJarsError] = useState('');

  // Options under the selector. All default on so a server behaves the way servers did
  // before these existed.
  const [bedrockEnabled, setBedrockEnabled] = useState(true);
  const [crackedEnabled, setCrackedEnabled] = useState(false);
  const [installViaVersion, setInstallViaVersion] = useState(true);
  const [installVoiceChat, setInstallVoiceChat] = useState(true);

  const [selectedPlan, setSelectedPlan] = useState('free');
  const [standalone, setStandalone] = useState(false);
  const [outOfStockPlans, setOutOfStockPlans] = useState<string[]>([]);
  const [ramGb, setRamGb] = useState(4);
  const [storageGb, setStorageGb] = useState(25);
  const [cpuCores, setCpuCores] = useState(4);
  const [selectedRegion, setSelectedRegion] = useState('NA');
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [hasCloudBackup, setHasCloudBackup] = useState(true);

  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoValidation, setPromoValidation] = useState<any>(null);
  const [promoError, setPromoError] = useState('');

  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const [showBackupWarningModal, setShowBackupWarningModal] = useState(false);

  // Loaders and their Minecraft versions, from the same catalog the in-panel version
  // switcher uses, so the two can't drift apart.
  useEffect(() => {
    let cancelled = false;
    setJarsLoading(true);
    fetch('/api/hosting/jars')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(t('failedToLoadVersions')))))
      .then((data) => {
        if (cancelled) return;
        setJarCatalog(Array.isArray(data?.jars) ? data.jars : []);
        setJarsError('');
      })
      .catch(() => {
        if (!cancelled) setJarsError(t('couldNotLoadMinecraft'));
      })
      .finally(() => {
        if (!cancelled) setJarsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // NeoForge cannot preinstall a server-side Via mod (see supportsViaVersion).
  const viaVersionAvailable = useMemo(() => supportsViaVersion(selectedJarType), [selectedJarType]);

  const selectedJar = useMemo(
    () => jarCatalog.find((jar: any) => jar.id === selectedJarType) || null,
    [jarCatalog, selectedJarType]
  );
  const jarVersions = useMemo(() => selectedJar?.versions || [], [selectedJar]);

  // Default to the newest stable version a loader offers (skipping release candidates and
  // pre-releases listed above it), and drop a version that the newly picked loader does not have.
  useEffect(() => {
    if (jarVersions.length === 0) return;
    if (!jarVersions.some((v: any) => v.id === selectedMcVersion)) {
      const stable = jarVersions.find((v: any) => !String(v.id).includes('-'));
      setSelectedMcVersion((stable || jarVersions[0]).id);
    }
  }, [jarVersions, selectedMcVersion]);

  const serverDomain = settings.hosting?.server_domain || 'apect.xyz';
  const plans = (settings.hosting?.plans || {}) as Record<string, any>;
  const pricing = (settings.hosting?.pricing || {}) as Record<string, any>;

  const selectedPlanConfig = plans[selectedPlan] || {};

  const monthlyPrice = calculatePrice(selectedPlan, ramGb, storageGb, cpuCores, plans, pricing);

  const finalPrice = useMemo(() => {
    if (promoValidation?.valid && typeof promoValidation?.finalPrice === 'number') {
      return promoValidation.finalPrice;
    }
    return monthlyPrice;
  }, [promoValidation, monthlyPrice]);

  const ramMin = selectedPlan === 'free' ? plans.free?.ram || 3 : selectedPlanConfig?.ram?.min || 1;
  const ramMax = selectedPlan === 'free' ? plans.free?.ram || 3 : selectedPlanConfig?.ram?.max || 32;
  const storageMin = selectedPlan === 'free' ? plans.free?.storage || 6 : selectedPlanConfig?.storage?.min || 25;
  const storageMax = selectedPlan === 'free' ? plans.free?.storage || 6 : selectedPlanConfig?.storage?.max || 600;
  const cpuMin = selectedPlan === 'free' ? plans.free?.cpu || 4 : selectedPlanConfig?.cpu?.min || 4;
  const cpuMax = selectedPlan === 'free' ? plans.free?.cpu || 4 : selectedPlanConfig?.cpu?.max || 16;

  // Options the three creation paths (free / Stripe / PayPal) all send.
  const createOptionsPayload = useMemo(() => ({
    jarType: selectedJarType,
    mcVersion: selectedMcVersion,
    bedrockEnabled,
    crackedEnabled,
    installViaVersion: viaVersionAvailable ? installViaVersion : false,
    installVoiceChat,
  }), [selectedJarType, selectedMcVersion, bedrockEnabled, crackedEnabled, installViaVersion, installVoiceChat, viaVersionAvailable]);

  const canProceedStep1 =
    serverName.length >= 3 &&
    nameAvailable === true &&
    // Templates aren't available yet, so only the version path can continue.
    (baseMode === 'version' ? Boolean(selectedJarType) : false);
  const canProceedStep2 =
    Boolean(selectedPlan) &&
    !outOfStockPlans.includes(selectedPlan) &&
    (selectedPlan === 'free' ||
      (paymentMethod === 'stripe'
        ? Boolean(stripeConfig.enabled && stripeConfig.publishableKey)
        : Boolean(settings.hosting?.paypal?.enabled)));

  const fetchClientSecret = useCallback(async () => {
    const response = await fetch('/api/hosting/payments/stripe/create-embed-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        serverName,
        plan: selectedPlan,
        ramGb,
        storageGb,
        cpuCores,
        ...createOptionsPayload,
        isPublic,
        standalone,
        monthlyPrice,
        region: selectedRegion,
        promoCode: promoValidation?.valid ? promoCode.trim() : null,
      }),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || t('failedToCreateCheckout'));
    }

    return data.clientSecret as string;
  }, [
    cpuCores,
    createOptionsPayload,
    isPublic,
    monthlyPrice,
    promoCode,
    promoValidation,
    ramGb,
    selectedPlan,
    selectedRegion,
    serverName,
    standalone,
    storageGb,
  ]);

  useEffect(() => {
    fetch('/api/config/stripe')
      .then((response) => response.json())
      .then((data) => {
        setStripeConfig(data);
        if (data.enabled && data.publishableKey) {
          setStripePromise(loadStripe(data.publishableKey));
        }
      })
      .catch((fetchError) => {
        console.error('Failed to load Stripe config:', fetchError);
      });
  }, []);

  useEffect(() => {
    fetch(`/api/hosting/plan-stock?region=${selectedRegion}`)
      .then((response) => response.json())
      .then((data) => {
        setOutOfStockPlans(data.outOfStock || []);
      })
      .catch((fetchError) => {
        console.error('Failed to fetch plan stock:', fetchError);
      });
  }, [selectedRegion]);

  useEffect(() => {
    if (showCheckoutModal && !clientSecret) {
      setCheckoutLoading(true);
      fetchClientSecret()
        .then((secret) => {
          setClientSecret(secret);
        })
        .catch((fetchError) => {
          const message = (fetchError && fetchError.message)
            ? String(fetchError.message)
            : t('paymentInitFailed');
          setError(message);
          setShowCheckoutModal(false);
        })
        .finally(() => {
          setCheckoutLoading(false);
        });
    }
  }, [clientSecret, fetchClientSecret, showCheckoutModal]);

  useEffect(() => {
    if (!showCheckoutModal) {
      setClientSecret(null);
    }
  }, [showCheckoutModal]);

  useEffect(() => {
    fetch('/api/hosting/cloud-accounts')
      .then((response) => response.json())
      .then((data) => {
        setHasCloudBackup(data.accounts?.length > 0);
      })
      .catch(() => {
        setHasCloudBackup(false);
      });
  }, []);

  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      setError(t('paymentWasCancelledPlease'));
    }
    // Prefill name from ?name=... (used by panel "Recreate" button after auto-deletion).
    const prefillName = searchParams.get('name');
    if (prefillName && /^[a-z0-9-]{3,32}$/i.test(prefillName)) {
      setServerName(prefillName.toLowerCase());
    }
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (serverName.length < 3) {
        setNameAvailable(null);
        setNameError('');
        return;
      }

      setCheckingName(true);
      try {
        const result = await apiCheckServerName(serverName);
        setNameAvailable(result.available);
        setNameError(result.reason || '');
      } catch (requestError: any) {
        setNameAvailable(false);
        setNameError(requestError?.message || t('failedToValidateName'));
      } finally {
        setCheckingName(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [serverName]);

  useEffect(() => {
    const planConfig = plans[selectedPlan];
    if (!planConfig) {
      return;
    }

    if (selectedPlan === 'free') {
      setRamGb(planConfig.ram);
      setStorageGb(planConfig.storage);
      setCpuCores(planConfig.cpu);
      setStandalone(false);
      return;
    }

    if (planConfig.configurable) {
      setRamGb(planConfig.ram.default);
      setStorageGb(planConfig.storage.default);
      setCpuCores(planConfig.cpu.default);
    }
  }, [plans, selectedPlan]);

  useEffect(() => {
    setPromoValidation(null);
    setPromoError('');
  }, [monthlyPrice, selectedPlan]);

  async function validatePromoCode() {
    if (!promoCode.trim()) {
      setPromoValidation(null);
      setPromoError('');
      return;
    }

    setPromoValidating(true);
    setPromoError('');

    try {
      const response = await fetch('/api/hosting/payments/stripe/validate-promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promoCode: promoCode.trim(),
          plan: selectedPlan,
          monthlyPrice,
        }),
      });

      const data = await response.json();
      if (data.valid) {
        setPromoValidation(data);
        setPromoError('');
      } else {
        setPromoValidation(null);
        setPromoError(data.error || t('invalidPromoCode'));
      }
    } catch (_error) {
      setPromoValidation(null);
      setPromoError(t('failedToValidatePromo'));
    } finally {
      setPromoValidating(false);
    }
  }

  function handleNext() {
    if (currentStep === 0 && canProceedStep1) {
      setCurrentStep(1);
      return;
    }

    if (currentStep === 1 && canProceedStep2) {
      setCurrentStep(2);
    }
  }

  function handleBack() {
    if (currentStep > 0) {
      setCurrentStep((previous) => previous - 1);
    }
  }

  async function handleCreate() {
    if (!hasCloudBackup) {
      setShowBackupWarningModal(true);
      return;
    }

    await proceedWithServerCreation();
  }

  async function proceedWithServerCreation() {
    setLoading(true);
    setError('');
    setShowBackupWarningModal(false);

    try {
      const stockResponse = await fetch(`/api/hosting/plan-stock?region=${selectedRegion}`);
      const stockData = await stockResponse.json();
      const currentOutOfStock = stockData.outOfStock || [];

      if (currentOutOfStock.includes(selectedPlan)) {
        setOutOfStockPlans(currentOutOfStock);
        setError(
          `Sorry, ${plans[selectedPlan]?.name || selectedPlan} servers are currently at full capacity. Please try a different plan or check back later.`
        );
        setLoading(false);
        return;
      }

      if (selectedPlan === 'free') {
        const result = await apiCreateServer({
          name: serverName,
          plan: 'free',
          ...createOptionsPayload,
          ramGb: plans.free?.ram || 3,
          storageGb: plans.free?.storage || 6,
          cpuCores: plans.free?.cpu || 4,
          isPublic,
          standalone: false,
          region: selectedRegion,
        });

        localStorage.setItem('has-created-server', 'true');
        // Unlinked users without a paid server may be placed in the creation queue
        if (result.queued) {
          router.push(`/panel/server/${result.server.name}/queue`);
          return;
        }
        router.push(`/panel/server/${result.server.name}/general?created=true`);
        return;
      }

      if (paymentMethod === 'stripe') {
        setShowCheckoutModal(true);
        setLoading(false);
        return;
      }

      if (paymentMethod === 'paypal') {
        const subscription = await apiCreatePayPalSubscription({
          serverName,
          plan: selectedPlan,
          ramGb,
          storageGb,
          cpuCores,
          ...createOptionsPayload,
          isPublic,
          standalone,
          monthlyPrice,
          region: selectedRegion,
        });

        if (subscription.approvalUrl) {
          window.location.href = subscription.approvalUrl;
        }
      }
    } catch (requestError: any) {
      setError(requestError?.message || t('failedToCreateServer'));
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-[calc(100vh-3.5rem)] overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_45%_at_50%_0%,hsl(var(--primary)/0.24),transparent_70%)]" />
      <div className="pointer-events-none absolute -top-12 left-[8%] h-44 w-44 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute right-[6%] top-24 h-52 w-52 rounded-full bg-secondary/40 blur-3xl" />

      <div className="relative mx-auto w-full max-w-6xl px-4 pb-16 pt-24 sm:px-6">
        <div className="mb-8 flex flex-col gap-3">
          <Badge className="w-fit">{t('serverBuilder')}</Badge>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{t('createYourMinecraftServer')}</h1>
          <p className="max-w-2xl text-muted-foreground">
            {t('launchAFreeServer')}
          </p>
        </div>

        {error ? (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('unableToContinue')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="mb-6 grid gap-3 md:grid-cols-3">
          {steps.map((step, index) => {
            const isActive = currentStep === index;
            const isComplete = currentStep > index;
            const isFuture = currentStep < index;

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => {
                  if (!isFuture) {
                    setCurrentStep(index);
                  }
                }}
                className={cn('text-left', isFuture && 'cursor-not-allowed')}
                disabled={isFuture}
              >
                <Card
                  className={cn(
                    'h-full border-border/70 bg-card/70 transition-colors',
                    isActive && 'border-primary/70 bg-primary/5',
                    isComplete && 'border-emerald-500/40 bg-emerald-500/10'
                  )}
                >
                  <CardContent className="flex items-center gap-3 p-4">
                    <div
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold',
                        isComplete && 'border-emerald-500/70 bg-emerald-500/20 text-emerald-100',
                        isActive && 'border-primary/70 bg-primary/20 text-primary-foreground',
                        isFuture && 'border-border/70 text-muted-foreground'
                      )}
                    >
                      {isComplete ? <Check className="h-4 w-4" /> : <span>{index + 1}</span>}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Step {index + 1}</p>
                      <p className="truncate text-sm font-medium">{tl(step.label)}</p>
                    </div>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>

        <Card className="mb-6 border-border/70 bg-card/80 backdrop-blur">
          <CardContent className="p-5 sm:p-6">
            {currentStep === 0 && (
              <div className="space-y-8">
                <section className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="server-name" className="text-sm font-medium">
                      {t('serverName')}
                    </Label>
                    <p className="text-sm text-muted-foreground">{t('pickAUniqueSubdomain')}</p>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="relative flex-1">
                      <Input
                        id="server-name"
                        className="h-10 pr-10"
                        placeholder="myserver"
                        value={serverName}
                        onChange={(event) => {
                          const nextValue = event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                          setServerName(nextValue);
                        }}
                      />
                      <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {checkingName ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : nameAvailable === true ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        ) : nameAvailable === false ? (
                          <X className="h-4 w-4 text-destructive" />
                        ) : null}
                      </div>
                    </div>
                    <div className="rounded-md border border-border/70 bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                      .{serverDomain}
                    </div>
                  </div>

                  {nameError ? (
                    <p className="text-xs text-destructive">{nameError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t('t332CharactersLowercase')}
                    </p>
                  )}
                </section>

                <section className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">{t('visibility')}</Label>
                    <p className="text-sm text-muted-foreground">{t('controlWhetherYourServer')}</p>
                  </div>

                  <RadioGroup
                    value={isPublic ? 'public' : 'private'}
                    onValueChange={(value) => setIsPublic(value === 'public')}
                    className="grid gap-3 sm:grid-cols-2"
                  >
                    <label
                      htmlFor="visibility-private"
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
                        !isPublic ? 'border-primary/70 bg-primary/5' : 'border-border/70 hover:bg-muted/30'
                      )}
                    >
                      <RadioGroupItem id="visibility-private" value="private" className="mt-0.5" />
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Lock className="h-4 w-4" />
                          {t('private')}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('hiddenFromDiscoveryShare')}
                        </p>
                      </div>
                    </label>

                    <label
                      htmlFor="visibility-public"
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
                        isPublic ? 'border-primary/70 bg-primary/5' : 'border-border/70 hover:bg-muted/30'
                      )}
                    >
                      <RadioGroupItem id="visibility-public" value="public" className="mt-0.5" />
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Globe className="h-4 w-4" />
                          {t('public')}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('listedInDiscovery')}
                        </p>
                      </div>
                    </label>
                  </RadioGroup>
                </section>

                <section className="space-y-3">
                  {/* Base selector: spans the whole card, and decides what the rows below show. */}
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">{t('base')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t('startFromAMinecraft')}
                    </p>
                  </div>

                  <div className="relative grid grid-cols-2 gap-2 rounded-xl border border-border/70 bg-muted/30 p-1">
                    {/* Sliding highlight behind the active option. */}
                    <span
                      aria-hidden
                      className={cn(
                        'pointer-events-none absolute inset-y-1 left-1 w-[calc(50%-0.5rem)] rounded-lg bg-background shadow-sm transition-transform duration-300 ease-out',
                        baseMode === 'template' && 'translate-x-[calc(100%+0.5rem)]'
                      )}
                    />
                    {([
                      { id: 'version', label: t('version') },
                      { id: 'template', label: t('template') },
                    ] as const).map((mode) => (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => setBaseMode(mode.id)}
                        className={cn(
                          'relative z-10 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors duration-300',
                          baseMode === mode.id
                            ? 'text-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>

                  {baseMode === 'version' ? (
                    <div key="version" className="rounded-xl border border-border/70 animate-in fade-in slide-in-from-bottom-1 duration-300">
                      <div className="grid gap-0 sm:grid-cols-2">
                        {/* Left: the loader. Right: the Minecraft version it runs. */}
                        <div className="space-y-2 border-b border-border/60 p-4 sm:border-b-0 sm:border-r">
                          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                            {t('serverSoftware')}
                          </Label>
                          {jarsLoading ? (
                            <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              {t('loadingVersions')}
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              {(jarCatalog.length > 0
                                ? jarCatalog
                                : [{ id: 'paper', name: 'Paper', description: t('latestRelease') }]
                              ).map((jar: any) => (
                                <button
                                  key={jar.id}
                                  type="button"
                                  onClick={() => setSelectedJarType(jar.id)}
                                  className={cn(
                                    'w-full rounded-lg border px-3 py-2 text-left transition-colors',
                                    selectedJarType === jar.id
                                      ? 'border-primary/70 bg-primary/5'
                                      : 'border-border/70 bg-card hover:bg-muted/40'
                                  )}
                                >
                                  <div className="flex items-center gap-3">
                                    {/* Same icons as the in-panel version switcher. */}
                                    <img
                                      src={`/jars/${jar.id}.png`}
                                      alt=""
                                      className="h-8 w-8 shrink-0 rounded-md object-cover"
                                      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                                    />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium">{jar.name}</span>
                                        {selectedJarType === jar.id ? (
                                          <Check className="h-4 w-4 shrink-0 text-primary" />
                                        ) : null}
                                      </div>
                                      <p className="line-clamp-1 text-xs text-muted-foreground">
                                        {jar.description}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="space-y-2 p-4">
                          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                            {t('minecraftVersion')}
                          </Label>
                          {jarsLoading ? (
                            <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              {t('loading')}
                            </div>
                          ) : jarVersions.length === 0 ? (
                            <p className="py-2 text-sm text-muted-foreground">
                              {t('noVersionsAvailableRight')}
                            </p>
                          ) : (
                            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
                              {jarVersions.map((version: any) => (
                                <button
                                  key={version.id}
                                  type="button"
                                  onClick={() => setSelectedMcVersion(version.id)}
                                  className={cn(
                                    'flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                                    selectedMcVersion === version.id
                                      ? 'border-primary/70 bg-primary/5 font-medium'
                                      : 'border-border/70 bg-card hover:bg-muted/40'
                                  )}
                                >
                                  <span>{version.label || version.id}</span>
                                  {selectedMcVersion === version.id ? (
                                    <Check className="h-4 w-4 shrink-0 text-primary" />
                                  ) : null}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key="template" className="rounded-xl border border-dashed border-border/70 bg-muted/20 p-8 text-center animate-in fade-in slide-in-from-bottom-1 duration-300">
                      <Badge variant="secondary" className="mb-3">{t('comingSoon')}</Badge>
                      <p className="text-sm font-medium">{t('templatesAreOnThe')}</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {t('prebuiltSetupsLikeLifesteal')}
                      </p>
                    </div>
                  )}

                  {jarsError ? (
                    <p className="text-xs text-amber-500">{jarsError}</p>
                  ) : null}

                  {/* Options that apply to the server itself, not to the jar. */}
                  <div className="space-y-3 rounded-xl border border-border/70 p-4">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      {t('options')}
                    </Label>

                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium">{t('bedrockPlayers')}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('allowBedrockEditionPlayers')}
                        </p>
                      </div>
                      <BooleanToggle checked={bedrockEnabled} onChange={setBedrockEnabled} />
                    </div>

                    <Separator />
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium">{t('crackedPlayers')}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('crackedDesc')}
                        </p>
                      </div>
                      <BooleanToggle checked={crackedEnabled} onChange={setCrackedEnabled} />
                    </div>

                    {/* Both extras are jar-level and meaningless until templates ship with
                        their own plugin sets, so they are hidden in template mode. */}
                    {baseMode === 'version' ? (
                      <>
                        {/* NeoForge has no server-side Via implementation - ViaForge and
                            ViaNeoForgePlus are client-side only - so for those servers
                            multi-version support comes from the proxy instead. */}
                        {viaVersionAvailable ? (
                          <>
                            <Separator />
                            <div className="flex items-start justify-between gap-4">
                              <div className="space-y-0.5">
                                <p className="text-sm font-medium">{t('preinstallViaversion')}</p>
                                <p className="text-xs text-muted-foreground">
                                  {t('letsPlayersOnOther')}
                                </p>
                              </div>
                              <BooleanToggle checked={installViaVersion} onChange={setInstallViaVersion} />
                            </div>
                          </>
                        ) : (
                          <>
                            <Separator />
                            <p className="text-xs text-muted-foreground">
                              {t('multiVersionSupportIs')}
                            </p>
                          </>
                        )}

                        <Separator />
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-0.5">
                            <p className="text-sm font-medium">{t('preinstallSimpleVoiceChat')}</p>
                            <p className="text-xs text-muted-foreground">
                              {t('proximityVoiceChatFor')}
                            </p>
                          </div>
                          <BooleanToggle checked={installVoiceChat} onChange={setInstallVoiceChat} />
                        </div>
                      </>
                    ) : null}
                  </div>

                  {standalone ? (
                    <p className="text-xs text-muted-foreground">
                      {t('standaloneModeIsEnabled')}
                    </p>
                  ) : null}
                </section>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-8">
                <section className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">{t('region')}</Label>
                    <p className="text-sm text-muted-foreground">{t('chooseWhereYourServer')}</p>
                  </div>

                  <RadioGroup
                    value={selectedRegion}
                    onValueChange={setSelectedRegion}
                    className="grid gap-3 sm:grid-cols-2"
                  >
                    <label
                      htmlFor="region-na"
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
                        selectedRegion === 'NA' ? 'border-primary/70 bg-primary/5' : 'border-border/70 hover:bg-muted/30'
                      )}
                    >
                      <RadioGroupItem id="region-na" value="NA" className="mt-0.5" />
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <MapPin className="h-4 w-4" />
                          {t('northAmerica')}
                        </div>
                        <p className="text-xs text-muted-foreground">{t('optimizedForUsAnd')}</p>
                      </div>
                    </label>

                    <label
                      htmlFor="region-eu"
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
                        selectedRegion === 'EU' ? 'border-primary/70 bg-primary/5' : 'border-border/70 hover:bg-muted/30'
                      )}
                    >
                      <RadioGroupItem id="region-eu" value="EU" className="mt-0.5" />
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <MapPin className="h-4 w-4" />
                          {t('europe')}
                        </div>
                        <p className="text-xs text-muted-foreground">{t('lowerLatencyForEuropean')}</p>
                      </div>
                    </label>
                  </RadioGroup>
                </section>

                <section className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">{t('plan')}</Label>
                    <p className="text-sm text-muted-foreground">{t('selectYourBaselineCapacity')}</p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {Object.entries(plans).map(([key, plan]: [string, any]) => {
                      const isOOS = outOfStockPlans.includes(key);
                      const isSelected = selectedPlan === key && !isOOS;

                      return (
                        <button
                          key={key}
                          type="button"
                          disabled={isOOS}
                          onClick={() => {
                            if (!isOOS) {
                              setSelectedPlan(key);
                            }
                          }}
                          className={cn(
                            'rounded-xl border p-4 text-left transition-all',
                            isSelected ? 'border-primary/70 bg-primary/5 shadow-sm' : 'border-border/70 bg-card hover:bg-muted/30',
                            isOOS && 'cursor-not-allowed opacity-55'
                          )}
                        >
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold">{plan.name}</p>
                            {isOOS ? (
                              <Badge variant="destructive">{t('outOfStock')}</Badge>
                            ) : (
                              <Badge variant={key === 'free' ? 'secondary' : 'default'}>
                                {key === 'free' ? t('free') : `${formatMoney(plan.price_per_gb_ram)}/GB`}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {isOOS ? t('currentlyUnavailableInThis') : plan.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </section>

                <Card className="border-dashed border-border/70 bg-muted/20">
                  <CardContent className="flex items-start gap-3 p-4">
                    <Checkbox
                      id="standalone"
                      checked={standalone}
                      disabled={selectedPlan === 'free'}
                      onCheckedChange={(checked) => {
                        setStandalone(Boolean(checked));
                      }}
                      className="mt-0.5"
                    />
                    <div className="space-y-1">
                      <Label htmlFor="standalone" className="text-sm font-medium">
                        {t('standaloneServer')}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {t('launchWithoutTheApect')}
                      </p>
                      {selectedPlan === 'free' ? (
                        <p className="text-xs text-muted-foreground">{t('standaloneServersRequireA')}</p>
                      ) : standalone ? (
                        <p className="text-xs text-primary">{t('standaloneTemplateOverrideIs')}</p>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>

                <div className="grid gap-4 lg:grid-cols-[1.8fr_1fr]">
                  <Card className="border-border/70">
                    <CardHeader>
                      <CardTitle>{t('resourceConfiguration')}</CardTitle>
                      <CardDescription>
                        {t('tuneYourServerResources')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <ResourceSlider label={t('ram')} icon={<Server />} value={ramGb} onChange={setRamGb} min={ramMin} max={ramMax} unit="GB" disabled={selectedPlan === 'free'} />

                      <ResourceSlider label={t('storage')} icon={<Database />} value={storageGb} onChange={setStorageGb} min={storageMin} max={storageMax} step={25} unit="GB" disabled={selectedPlan === 'free'} />

                      <ResourceSlider label={t('cpu')} icon={<Cpu />} value={cpuCores} onChange={setCpuCores} min={cpuMin} max={cpuMax} unit="vCores" disabled={selectedPlan === 'free'} />

                      {selectedPlan === 'free' ? (
                        <Alert>
                          <Lock className="h-4 w-4" />
                          <AlertTitle>{t('freePlanIsFixed')}</AlertTitle>
                          <AlertDescription>
                            {t('upgradeToAPaid')}
                          </AlertDescription>
                        </Alert>
                      ) : null}
                    </CardContent>
                  </Card>

                  <Card className="border-border/70">
                    <CardHeader>
                      <CardTitle>{t('summary')}</CardTitle>
                      <CardDescription>{t('currentProvisioningEstimate')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('plan')}</span>
                        <Badge variant={selectedPlan === 'free' ? 'secondary' : 'default'}>
                          {plans[selectedPlan]?.name || selectedPlan}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('ram')}</span>
                        <span>{ramGb} GB</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('storage')}</span>
                        <span>{storageGb} GB</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('cpu')}</span>
                        <span>{cpuCores} vCores</span>
                      </div>
                      <Separator />
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{t('monthlySubtotal')}</span>
                        <span className="text-lg font-semibold">
                          {monthlyPrice === 0 ? t('free') : formatMoney(monthlyPrice)}
                        </span>
                      </div>
                      {promoValidation?.valid ? (
                        <div className="flex items-center justify-between text-emerald-300">
                          <span>{t('promoApplied')}</span>
                          <span>-{formatMoney(promoValidation.discountAmount || 0)}</span>
                        </div>
                      ) : null}
                    </CardContent>
                  </Card>
                </div>

                {selectedPlan !== 'free' ? (
                  <section className="space-y-4">
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">{t('paymentMethod')}</Label>
                      <p className="text-sm text-muted-foreground">{t('selectHowYouWant')}</p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        disabled={!stripeConfig.enabled || !stripeConfig.publishableKey}
                        onClick={() => {
                          if (stripeConfig.enabled && stripeConfig.publishableKey) {
                            setPaymentMethod('stripe');
                          }
                        }}
                        className={cn(
                          'rounded-xl border p-4 text-left transition-colors',
                          paymentMethod === 'stripe' ? 'border-primary/70 bg-primary/5' : 'border-border/70 bg-card hover:bg-muted/30',
                          (!stripeConfig.enabled || !stripeConfig.publishableKey) && 'cursor-not-allowed opacity-60'
                        )}
                      >
                        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                          <CreditCard className="h-4 w-4" />
                          {t('creditOrDebitCard')}
                        </div>
                        <div className="mb-2 flex flex-wrap gap-1.5 text-xs text-muted-foreground">
                          <Badge variant="outline">{t('visa')}</Badge>
                          <Badge variant="outline">{t('mastercard')}</Badge>
                          <Badge variant="outline">{t('amex')}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {stripeConfig.enabled && stripeConfig.publishableKey ? t('secureCheckoutPoweredBy') : t('comingSoon2')}
                        </p>
                      </button>

                      <button
                        type="button"
                        disabled={!settings.hosting?.paypal?.enabled}
                        onClick={() => {
                          if (settings.hosting?.paypal?.enabled) {
                            setPaymentMethod('paypal');
                          }
                        }}
                        className={cn(
                          'rounded-xl border p-4 text-left transition-colors',
                          paymentMethod === 'paypal' ? 'border-primary/70 bg-primary/5' : 'border-border/70 bg-card hover:bg-muted/30',
                          !settings.hosting?.paypal?.enabled && 'cursor-not-allowed opacity-60'
                        )}
                      >
                        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                          <CreditCard className="h-4 w-4" />
                          {t('paypal')}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {settings.hosting?.paypal?.enabled ? t('fastCheckoutThroughYour') : t('comingSoon2')}
                        </p>
                      </button>
                    </div>

                    <Card className="border-border/70">
                      <CardContent className="space-y-3 p-4">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Ticket className="h-4 w-4" />
                          {t('promoCode')}
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Input
                            placeholder={t('enterPromoCode')}
                            value={promoCode}
                            onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                          />
                          <Button
                            variant="outline"
                            onClick={() => {
                              void validatePromoCode();
                            }}
                            disabled={!promoCode.trim() || promoValidating}
                          >
                            {promoValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            Apply
                          </Button>
                          {promoValidation?.valid ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setPromoCode('');
                                setPromoValidation(null);
                                setPromoError('');
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>

                        {promoError ? <p className="text-xs text-destructive">{promoError}</p> : null}

                        {promoValidation?.valid ? (
                          <Alert className="border-emerald-500/30 bg-emerald-500/10">
                            <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                            <AlertTitle className="text-emerald-200">{t('promoApplied')}</AlertTitle>
                            <AlertDescription className="text-emerald-100/80">
                              {promoValidation.discountType === 'percent'
                                ? `${promoValidation.discountValue}% off`
                                : `${formatMoney(promoValidation.discountValue || 0)} off`}
                              {promoValidation.durationMonths > 1 ? ` for ${promoValidation.durationMonths} months` : ''}
                              {promoValidation.freeTrialDays ? ` + ${promoValidation.freeTrialDays} day free trial` : ''}
                            </AlertDescription>
                          </Alert>
                        ) : null}
                      </CardContent>
                    </Card>
                  </section>
                ) : null}
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <h2 className="text-2xl font-semibold tracking-tight">{t('reviewAndLaunch')}</h2>
                  <p className="text-sm text-muted-foreground">
                    {t('confirmYourServerSetup')}
                  </p>
                </div>

                {selectedPlan === 'free' && !hasCloudBackup ? (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{t('protectYourServerData')}</AlertTitle>
                    <AlertDescription>
                      {t('youDoNotHave')}
                    </AlertDescription>
                  </Alert>
                ) : null}

                <div className="grid gap-4 md:grid-cols-2">
                  <Card className="border-border/70">
                    <CardHeader>
                      <CardTitle>{t('configuration')}</CardTitle>
                      <CardDescription>
                        {serverName || 'your-server'}.{serverDomain}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('version')}</span>
                        <span className="font-medium">
                          {standalone
                            ? t('standalone')
                            : `${selectedJar?.name || selectedJarType}${selectedMcVersion ? ` ${selectedMcVersion}` : ''}`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('bedrockPlayers')}</span>
                        <span className="font-medium">{bedrockEnabled ? t('allowed') : t('blocked')}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('crackedPlayers')}</span>
                        <span className="font-medium">{crackedEnabled ? t('allowed') : t('blocked')}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('preinstalled')}</span>
                        <span className="font-medium">
                          {/* Mirror what is actually sent: NeoForge never gets ViaVersion. */}
                          {[
                            viaVersionAvailable && installViaVersion ? 'ViaVersion' : null,
                            installVoiceChat ? t('voiceChat') : null,
                          ].filter(Boolean).join(', ') || t('none')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('visibility')}</span>
                        <span className="font-medium">{isPublic ? t('public') : t('private')}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('region')}</span>
                        <span className="font-medium">{selectedRegion === 'NA' ? t('northAmerica') : t('europe')}</span>
                      </div>
                      <Separator />
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('ram')}</span>
                        <span className="font-medium">{ramGb} GB</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('storage')}</span>
                        <span className="font-medium">{storageGb} GB</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('cpu')}</span>
                        <span className="font-medium">{cpuCores} vCores</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-border/70">
                    <CardHeader>
                      <CardTitle>{t('billing')}</CardTitle>
                      <CardDescription>
                        {selectedPlan === 'free' ? t('noPaymentRequired') : paymentMethod === 'stripe' ? t('paymentViaCard') : t('paymentViaPaypal')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('plan')}</span>
                        <Badge variant={selectedPlan === 'free' ? 'secondary' : 'default'}>
                          {plans[selectedPlan]?.name || selectedPlan}
                        </Badge>
                      </div>

                      {promoValidation?.valid && selectedPlan !== 'free' ? (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t('subtotal')}</span>
                            <span className="line-through">{formatMoney(monthlyPrice)}/mo</span>
                          </div>
                          <div className="flex items-center justify-between text-emerald-300">
                            <span>Promo {promoCode}</span>
                            <span>-{formatMoney(promoValidation.discountAmount || 0)}</span>
                          </div>
                          {promoValidation.freeTrialDays > 0 ? (
                            <div className="flex items-center justify-between text-primary">
                              <span>{t('freeTrial')}</span>
                              <span>{promoValidation.freeTrialDays} days</span>
                            </div>
                          ) : null}
                        </>
                      ) : null}

                      <Separator />
                      <div className="flex items-center justify-between">
                        <span className="text-base font-semibold">{t('monthlyTotal')}</span>
                        <span className="text-xl font-semibold">
                          {finalPrice === 0 ? t('free') : `${formatMoney(finalPrice)}/mo`}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Button
                  size="lg"
                  className="w-full bg-gradient-to-r from-primary to-primary/80 text-primary-foreground"
                  onClick={() => {
                    void handleCreate();
                  }}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {selectedPlan === 'free' ? t('createServer') : t('proceedToPayment')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 0 || loading || checkoutLoading}>
            <ArrowLeft className="h-4 w-4" />
            {t('back')}
          </Button>

          {currentStep < 2 ? (
            <Button
              className="group"
              onClick={handleNext}
              disabled={
                loading ||
                (currentStep === 0 && !canProceedStep1) ||
                (currentStep === 1 && !canProceedStep2)
              }
            >
              {t('next')}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
            </Button>
          ) : null}
        </div>
      </div>

      <Dialog open={showBackupWarningModal} onOpenChange={setShowBackupWarningModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('protectYourServerData')}</DialogTitle>
            <DialogDescription>
              {t('youDoNotHave2')}
            </DialogDescription>
          </DialogHeader>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('recommended')}</AlertTitle>
            <AlertDescription>
              {t('linkGoogleDriveNow')}
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                void proceedWithServerCreation();
              }}
            >
              {t('continueWithoutBackup')}
            </Button>
            <Button
              onClick={() => {
                setShowBackupWarningModal(false);
                window.location.href = '/account?tab=cloud-backup';
              }}
            >
              {t('linkGoogleDrive')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCheckoutModal} onOpenChange={setShowCheckoutModal}>
        <DialogContent className="max-w-5xl p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle>{t('completeYourPurchase')}</DialogTitle>
            <DialogDescription>
              {t('creatingServer', { plan: plans[selectedPlan]?.name || selectedPlan, domain: `${serverName}.${serverDomain}`, promo: promoValidation?.valid ? t('withPromo', { code: promoCode }) : '' })}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[72vh] overflow-auto px-6 pb-6">
            {clientSecret && stripePromise ? (
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            ) : (
              <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin" />
                <p>{checkoutLoading ? t('preparingSecurePayment') : t('waitingForCheckoutSession')}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CreatePage() {
  const t = useTranslations('Auto.Create');
  const tl = useLabel();
  return (
    <AdWrapper showSideAds={true} showLeftSideAd={true} showBottomAd={true}>
    <Suspense
      fallback={
        <div className="mx-auto flex min-h-[60vh] max-w-3xl items-center justify-center px-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            {t('loadingServerBuilder')}
          </div>
        </div>
      }
    >
      <CreatePageContent />
    </Suspense>
    </AdWrapper>
  );
}